import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { httpsCallable } from "firebase/functions";
import { doc, onSnapshot } from "firebase/firestore";
import { db, functions } from "./firebase";

type Section = "goal" | "home" | "car" | "general";
type Plan = Record<string, string | number>;
type Plans = Partial<Record<Section, Plan | null>>;
type Scope = { householdId: string; financeProfileUid: string; scope: "household" | "profile" };
const savePlan = httpsCallable<Scope & { section: Section; value: Plan | null }, { value: Plan | null }>(functions, "saveFinancePlanning");
const Context = createContext<{ plans: Plans; save: (section: Section, value: Plan | null) => Promise<void>; canSave: boolean } | null>(null);

export function FinancePlanningProvider({ householdId, financeProfileUid, scope, canSave, children }: Scope & { canSave: boolean; children: ReactNode }) {
  const [plans, setPlans] = useState<Plans>({});
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const saving = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    if (!householdId || !financeProfileUid) { setReady(false); setPlans({}); return () => { alive.current = false; }; }
    const stop = onSnapshot(doc(db, "households", householdId, "financeProfiles", financeProfileUid, "privatePlanning", scope), snapshot => {
      if (alive.current) { setPlans((snapshot.data() || {}) as Plans); setReady(true); setError(""); }
    }, () => {
      if (alive.current) { setPlans({}); setReady(false); setError("Your private plans are unavailable. Check your financial access and reopen Finances."); }
    });
    return () => { alive.current = false; stop(); };
  }, [householdId, financeProfileUid, scope]);
  const save = async (section: Section, value: Plan | null) => {
    if (!canSave || saving.current) return;
    saving.current = true; setBusy(true); setError("");
    try {
      const response = await savePlan({ householdId, financeProfileUid, scope, section, value });
      if (alive.current) setPlans(current => ({ ...current, [section]: response.data.value }));
    } catch { if (alive.current) setError("The plan was not saved. Check your financial access and try again."); }
    finally { saving.current = false; if (alive.current) setBusy(false); }
  };
  return <Context.Provider value={{ plans, save, canSave: canSave && !busy }}>
    {error && <p role="alert">{error}</p>}
    {ready ? children : !error && <p role="status">Loading private plans…</p>}
  </Context.Provider>;
}
export function useFinanceBaseline<T extends Plan>(section: Section) {
  const context = useContext(Context);
  if (!context) throw new Error("Private planning context is required.");
  return { baseline: (context.plans[section] || null) as T | null, saveBaseline: (value: T | null) => { void context.save(section, value); }, canSave: context.canSave };
}
